import { Effect } from 'effect';
import { attemptStudio, runStudioPromise, type StudioRuntimeError } from '../effect-runtime';
import { studioHttpEffect, readStudioJsonEffect } from './http-operation';

/**
 * Client for the shell's studio API (`/_emdash/api/studio/*`), which proxies
 * the compute worker. All routes require a signed-in EmDash session; mutations
 * additionally require the `x-emdash-request: 1` CSRF header.
 */

const API_BASE = '/_emdash/api/studio';

export class StudioApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'StudioApiError';
    this.status = status;
    this.code = code;
  }
}

export function isAuthError(error: unknown): boolean {
  return error instanceof StudioApiError && (error.status === 401 || error.status === 403);
}

function isJsonResponse(response: Response): boolean {
  const contentType = response.headers.get('content-type') ?? '';
  return contentType.includes('json');
}

const parseResponseEffect = Effect.fn('studio.parseResponse')(function* <T>(
  response: Response,
  fallback: string,
): Effect.fn.Return<T, StudioRuntimeError> {
  if (!response.ok) {
    const body = yield* readStudioJsonEffect(response).pipe(
      Effect.catch(() => Effect.succeed(null)),
    );
    return yield* attemptStudio('api', 'response-status', () => {
      const error = (body as { error?: { code?: string; message?: string } } | null)?.error;
      throw new StudioApiError(
        response.status,
        error?.code || 'request_failed',
        error?.message || `${fallback} (HTTP ${response.status})`,
      );
    });
  }
  yield* attemptStudio('api', 'response-content-type', () => {
    if (!isJsonResponse(response))
      throw new StudioApiError(
        response.status,
        'compute_unavailable',
        'The studio compute API is not available on this deployment.',
      );
  });
  const body = yield* readStudioJsonEffect(response);
  return body !== null && typeof body === 'object' && 'data' in body
    ? (body as { data: T }).data
    : (body as T);
});

export const apiGetEffect = Effect.fn('studio.apiGet')(function* <T>(
  path: string,
): Effect.fn.Return<T, StudioRuntimeError> {
  return yield* studioHttpEffect(`${API_BASE}${path}`, { credentials: 'same-origin' }, (response) =>
    parseResponseEffect<T>(response, `Failed to load ${path}`),
  );
});
export function apiGet<T>(path: string): Promise<T> {
  return runStudioPromise(apiGetEffect<T>(path));
}

export const apiPostEffect = Effect.fn('studio.apiPost')(function* <T>(
  path: string,
  body?: unknown,
): Effect.fn.Return<T, StudioRuntimeError> {
  const encoded = yield* attemptStudio('api', 'request-json', () =>
    body === undefined ? '{}' : JSON.stringify(body),
  );
  return yield* studioHttpEffect(
    `${API_BASE}${path}`,
    {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json', 'x-emdash-request': '1' },
      body: encoded,
    },
    (response) => parseResponseEffect<T>(response, 'Request failed'),
  );
});
export function apiPost<T>(path: string, body?: unknown): Promise<T> {
  return runStudioPromise(apiPostEffect<T>(path, body));
}

export const apiUploadPdfEffect = Effect.fn('studio.apiUploadPdf')(function* <T>(
  path: string,
  bytes: Uint8Array,
  fileName: string,
): Effect.fn.Return<T, StudioRuntimeError> {
  const init = yield* attemptStudio(
    'api',
    'upload-request',
    (): RequestInit => ({
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'content-type': 'application/pdf',
        'x-emdash-request': '1',
        'x-file-name': encodeURIComponent(fileName),
      },
      body: bytes.slice().buffer as ArrayBuffer,
    }),
  );
  return yield* studioHttpEffect(`${API_BASE}${path}`, init, (response) =>
    parseResponseEffect<T>(response, 'Upload failed'),
  );
});
export function apiUploadPdf<T>(path: string, bytes: Uint8Array, fileName: string): Promise<T> {
  return runStudioPromise(apiUploadPdfEffect<T>(path, bytes, fileName));
}

export interface SignedUpload {
  url: string;
  method: 'PUT';
  headers: Record<string, string>;
  expiresAt: number;
}

/**
 * Upload a PDF for parsing. Prefers a direct-to-R2 presigned PUT (bypasses the
 * Worker body / 64 MiB cap); falls back to the buffered upload when the backend
 * can't presign (no R2 S3 creds bound). Returns the created document id.
 */
export const uploadPdfForParseEffect = Effect.fn('studio.uploadPdfForParse')(function* (
  bytes: Uint8Array,
  fileName: string,
  parserId?: string,
): Effect.fn.Return<{ documentId: string }, StudioRuntimeError> {
  const presigned = yield* apiPostEffect<{ documentId: string; presign: SignedUpload | null }>(
    '/documents/presign',
    { fileName, contentType: 'application/pdf' },
  ).pipe(Effect.catch(() => Effect.succeed(null)));
  if (presigned?.presign && presigned.documentId) {
    const init = yield* attemptStudio(
      'api',
      'direct-upload-request',
      (): RequestInit => ({
        method: 'PUT',
        headers: presigned.presign!.headers,
        body: bytes.slice().buffer as ArrayBuffer,
      }),
    );
    yield* studioHttpEffect(presigned.presign.url, init, (put) =>
      attemptStudio('api', 'direct-upload-response', () => {
        if (!put.ok)
          throw new StudioApiError(
            put.status,
            'r2_put_failed',
            `Direct upload to storage failed (HTTP ${put.status}).`,
          );
      }),
    );
    yield* apiPostEffect(`/documents/${presigned.documentId}/ingest`, { fileName, parserId });
    return { documentId: presigned.documentId };
  }
  return yield* apiUploadPdfEffect<{ documentId: string }>('/documents', bytes, fileName);
});

export function uploadPdfForParse(
  bytes: Uint8Array,
  fileName: string,
  parserId?: string,
): Promise<{ documentId: string }> {
  return runStudioPromise(uploadPdfForParseEffect(bytes, fileName, parserId));
}

// ---- Shared AI-side types (mirror the compute worker's payloads) ----

export type RunStatus = 'idle' | 'running' | 'completed' | 'completed_with_errors' | 'failed';

const TERMINAL_STATUSES = new Set<RunStatus>(['completed', 'completed_with_errors', 'failed']);

/** Terminal parse states: the run is over (successfully or not). */
export function isTerminal(status: string): boolean {
  return Effect.runSync(isTerminalEffect(status));
}

export function isTerminalEffect(status: string): Effect.Effect<boolean> {
  return Effect.sync(() => TERMINAL_STATUSES.has(status as RunStatus));
}

/** The document's graph is usable for extract/verify/redact/export ops. */
export function isUsable(doc: StudioComputeDocument): boolean {
  return Effect.runSync(isUsableEffect(doc));
}

export function isUsableEffect(doc: StudioComputeDocument): Effect.Effect<boolean> {
  return Effect.sync(() => doc.status === 'completed' || doc.status === 'completed_with_errors');
}

export interface StudioComputeDocument {
  documentId: string;
  fileName: string | null;
  parserId: string;
  status: RunStatus;
  pagesTotal: number;
  pagesDone: number;
  pagesFailed: number;
  createdAt: number;
  updatedAt: number;
}

export interface GraphBlock {
  type: string;
  label?: string;
  value?: string;
  bbox?: { x: number; y: number; w: number; h: number };
  confidence?: number;
}

export interface StudioGraph {
  documentId: string;
  status: RunStatus;
  graphVersion: string;
  pages: Array<{ pageNumber: number; blocks: GraphBlock[] }>;
}

export interface OperationResult {
  operation?: string;
  graphVersion?: string;
  runId?: string;
  files?: Array<{ name: string; mimeType: string; text: string }>;
  [key: string]: unknown;
}
